import React, { useState, useEffect } from 'react';
import { Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import api from '../api';

const JobManagement = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [applicantCounts, setApplicantCounts] = useState({});
    const [categories, setCategories] = useState({
        JOB_FIELD: [],
        LOCATION: []
    });

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/categories');
                const allCategories = response.data;
                const grouped = {
                    JOB_FIELD: [],
                    LOCATION: []
                };
                allCategories.forEach(cat => {
                    if ((cat.type === 'JOB_FIELD' || cat.type === 'LOCATION') && cat.status) {
                        grouped[cat.type].push(cat);
                    }
                });
                setCategories(grouped);
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchJobs();
    }, [currentPage, pageSize]);

    const handleDelete = async (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa công việc này?')) {
            try {
                await api.delete(`/jobs/${id}`);
                fetchJobs();
            } catch (error) {
                console.error("Error deleting job:", error);
            }
        }
    };

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const response = await api.get('/jobs', {
                params: {
                    page: currentPage,
                    size: pageSize
                }
            });

            setJobs(response.data.content);
            setTotalPages(response.data.totalPages);
            setTotalElements(response.data.totalElements);

            // Fetch applicant counts for each job
            const counts = {};
            for (const job of response.data.content) {
                try {
                    const appResponse = await api.get(`/applications/job/${job.id}`);
                    counts[job.id] = appResponse.data.length;
                } catch (error) {
                    counts[job.id] = 0;
                }
            }
            setApplicantCounts(counts);

        } catch (error) {
            console.error("Error fetching jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="job-management">
            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search className="search-icon" size={20} />
                    <input
                        type="text"
                        placeholder="Tìm kiếm"
                        className="search-input"
                    />
                </div>
                <select className="filter-select">
                    <option value="">Lĩnh vực</option>
                    {categories.JOB_FIELD.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                </select>
                <select className="filter-select">
                    <option value="">Địa điểm</option>
                    {categories.LOCATION.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                </select>
                <button className="btn-primary" onClick={() => navigate('/jobs/create')}>
                    Tạo công việc mới
                </button>
            </div>

            <div className="data-table-container">
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>STT</th>
                                <th>Tên công việc</th>
                                <th>Công ty</th>
                                <th>Lĩnh vực</th>
                                <th>Địa điểm</th>
                                <th>Hạn ứng tuyển</th>
                                <th>Số lượng ứng viên</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job, index) => {
                                const applicantCount = applicantCounts[job.id] || 0;
                                const isExpired = new Date(job.deadline) < new Date();

                                return (
                                    <tr key={job.id}>
                                        <td>{currentPage * pageSize + index + 1}</td>
                                        <td>
                                            <div style={{ fontWeight: 500 }}>{job.title}</div>
                                        </td>
                                        <td>{job.company}</td>
                                        <td>{job.field}</td>
                                        <td>{job.location}</td>
                                        <td>
                                            <div>{job.deadline}</div>
                                            {isExpired && <span className="badge-expired">Đã hết hạn</span>}
                                        </td>
                                        <td>
                                            <div className="applicant-count">
                                                <span className="count-number">{applicantCount}</span>
                                                {applicantCount > 0 && (
                                                    <span className="badge-applicants">{applicantCount} ứng viên đã nộp</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className="action-link"
                                                onClick={() => navigate(`/jobs/${job.id}`)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                Chi tiết <ChevronRight size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div className="pagination">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Hiển thị
                        <select
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd' }}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(0);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                        trên 1 trang (Tổng: {totalElements})
                    </div>
                    <div className="page-numbers">
                        <button
                            className="page-btn"
                            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                            disabled={currentPage === 0}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i;
                            } else if (currentPage < 3) {
                                pageNum = i;
                            } else if (currentPage >= totalPages - 3) {
                                pageNum = totalPages - 5 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(pageNum)}
                                >
                                    {pageNum + 1}
                                </button>
                            );
                        })}
                        {totalPages > 5 && currentPage < totalPages - 3 && (
                            <>
                                <span style={{ display: 'flex', alignItems: 'end', paddingBottom: '4px' }}>...</span>
                                <button
                                    className="page-btn"
                                    onClick={() => setCurrentPage(totalPages - 1)}
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                        <button
                            className="page-btn"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                            disabled={currentPage === totalPages - 1}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobManagement;
